import { useEffect, useRef, useState } from "preact/hooks";
import { api, ApiError } from "../../api.js";
import { t } from "../../i18n/index.js";
import { playSound } from "../../audio/sounds.js";
import { refreshCoins } from "../../coins.js";
import { PlayingCard } from "../vale/ValeGame.js";
import {
  JOKER,
  PAYTABLE,
  POKER_BETS,
  POKER_HANDS,
  TOP_MULTIPLIER,
  type Card,
  type DoubleGuess,
} from "../../../shared/games/poker/index.js";
import type { PokerView as View } from "../../../shared/games/poker/index.js";

// Pokeriautomaatti (user request 2026-10-03): the RAY-style joker poker machine with doubling, played with
// AreCoin. The server deals and pays; this page shows the machine, plays the sounds and counts the win into
// the balance like the old machines. Works on phones too (user request): big buttons, cards scale.

const CARD_STEP_MS = 110;
const COUNT_STEP_MS = 45;
const SUSPENSE_MS = 650;
const errorText = (err: unknown) =>
  t.errors[err instanceof ApiError ? err.code : "network"];

function Joker() {
  return (
    <span class="pcard pk-joker" aria-label={t.poker.joker}>
      <span class="pk-joker-star">★</span>
      <span class="pk-joker-text">{t.poker.joker.toUpperCase()}</span>
    </span>
  );
}

const CardFace = (props: { card: Card }) =>
  props.card === JOKER ? <Joker /> : <PlayingCard card={props.card} />;

export function PokerView() {
  const [v, setV] = useState<View | null>(null);
  const [bet, setBet] = useState<number>(POKER_BETS[0]);
  const [held, setHeld] = useState<Set<number>>(new Set());
  const [doubling, setDoubling] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // The win being counted into the balance: [left to count, balance shown].
  const [counting, setCounting] = useState<{
    left: number;
    balance: number;
  } | null>(null);
  // Card animations: a new key makes a card come in again.
  const [dealNo, setDealNo] = useState(0);
  const [fresh, setFresh] = useState<Set<number>>(new Set());
  const [suspense, setSuspense] = useState(false);
  const countTimer = useRef<ReturnType<typeof setInterval>>();

  useEffect(() => {
    api<View>("GET", "/api/poker")
      .then((r) => {
        setV(r);
        setBet(r.bet);
      })
      .catch((e) => setError(errorText(e)));
    return () => clearInterval(countTimer.current);
  }, []);

  /** Counts a paid win into the balance shown, a coin tick per step. */
  const count = (paid: number, balanceAfter: number | null) => {
    if (paid <= 0 || balanceAfter === null) return void refreshCoins();
    clearInterval(countTimer.current);
    const steps = Math.min(paid, 40);
    const per = paid / steps;
    let done = 0;
    setCounting({ left: paid, balance: balanceAfter - paid });
    countTimer.current = setInterval(() => {
      done++;
      const counted = Math.min(paid, Math.round(done * per));
      playSound("pokerCoin");
      setCounting({
        left: paid - counted,
        balance: balanceAfter - paid + counted,
      });
      if (done >= steps) {
        clearInterval(countTimer.current);
        setCounting(null);
        void refreshCoins();
      }
    }, COUNT_STEP_MS);
  };

  const call = async (path: string, body?: unknown): Promise<View | null> => {
    setBusy(true);
    setError(null);
    try {
      return await api<View>("POST", path, body);
    } catch (e) {
      setError(errorText(e));
      return null;
    } finally {
      setBusy(false);
    }
  };

  const ticks = (n: number) => {
    for (let i = 0; i < n; i++)
      setTimeout(() => playSound("pokerDeal"), i * CARD_STEP_MS);
  };
  const winSound = (r: View) =>
    playSound(r.win >= 20 * r.bet ? "pokerBigWin" : "pokerWin");

  const deal = async () => {
    if (busy || counting) return;
    const r = await call("/api/poker/deal", { bet });
    if (!r) return;
    setV(r);
    setHeld(new Set());
    setDoubling(false);
    setMessage(null);
    setDealNo((n) => n + 1);
    setFresh(new Set([0, 1, 2, 3, 4]));
    ticks(5);
    if (r.paid > 0) count(r.paid, r.balance);
    else void refreshCoins();
  };

  const draw = async () => {
    if (busy || !v || v.phase !== "hold") return;
    const replaced = [0, 1, 2, 3, 4].filter((i) => !held.has(i));
    const r = await call("/api/poker/draw", { hold: [...held] });
    if (!r) return;
    setV(r);
    setDealNo((n) => n + 1);
    setFresh(new Set(replaced));
    ticks(replaced.length);
    setTimeout(
      () => {
        if (r.phase === "win") {
          winSound(r);
          setMessage(r.canDouble ? t.poker.winHint(r.win) : t.poker.topHint);
        } else setMessage(t.poker.noWin);
      },
      replaced.length * CARD_STEP_MS + 150,
    );
  };

  const guess = async (g: DoubleGuess) => {
    if (busy || !v || !doubling) return;
    setSuspense(true);
    playSound("pokerSuspense");
    const [r] = await Promise.all([
      call("/api/poker/double", { guess: g }),
      new Promise((ok) => setTimeout(ok, SUSPENSE_MS)),
    ]);
    setSuspense(false);
    if (!r || !r.double) return;
    setV(r);
    const { result, card } = r.double;
    if (result === "win") {
      playSound("pokerDoubleWin");
      setMessage(t.poker.doubleWin(r.win));
      if (!r.canDouble) setDoubling(false);
    } else if (result === "lose") {
      playSound("pokerLose");
      setMessage(
        card % 13 === 5 && card !== JOKER
          ? t.poker.blackSeven
          : t.poker.doubleLose,
      );
      setDoubling(false);
    } else {
      playSound("pokerPush");
      setMessage(t.poker.redSeven);
      setDoubling(false);
      count(r.paid, r.balance);
    }
  };

  const collect = async () => {
    if (busy || !v || v.phase !== "win") return;
    const r = await call("/api/poker/collect");
    if (!r) return;
    setV(r);
    setDoubling(false);
    setMessage(t.poker.paid(r.paid));
    count(r.paid, r.balance);
  };

  const toggle = (i: number) => {
    if (!v || v.phase !== "hold" || busy) return;
    playSound("pokerHold");
    setHeld((h) => {
      const next = new Set(h);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });
  };
  const nextBet = () => {
    if (v?.phase === "hold") return;
    playSound("pokerHold");
    setBet(
      (b) =>
        POKER_BETS[
          (POKER_BETS.indexOf(b as (typeof POKER_BETS)[number]) + 1) %
            POKER_BETS.length
        ]!,
    );
  };
  const startDouble = () => {
    if (!v?.canDouble || busy) return;
    playSound("pokerHold");
    setDoubling(true);
    setMessage(t.poker.doubleHint);
  };

  // The keyboard on a computer.
  const keys = useRef<(e: KeyboardEvent) => void>();
  keys.current = (e: KeyboardEvent) => {
    const target = e.target as HTMLElement | null;
    if (
      target &&
      (target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.tagName === "SELECT")
    )
      return;
    const k = e.key.toLowerCase();
    if (k >= "1" && k <= "5") toggle(Number(k) - 1);
    else if (k === "enter" || k === " ") {
      e.preventDefault();
      if (v?.phase === "hold") void draw();
      else if (!doubling) void deal();
    } else if (k === "t") startDouble();
    else if (k === "v") void collect();
    else if (k === "+" || k === "p") nextBet();
    else if (doubling && k === "arrowleft") void guess("small");
    else if (doubling && k === "arrowright") void guess("big");
  };
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => keys.current?.(e);
    addEventListener("keydown", onKey);
    return () => removeEventListener("keydown", onKey);
  }, []);

  const phase = v?.phase ?? "idle";
  const shownBet = phase === "idle" ? bet : (v?.bet ?? bet);
  const win = counting ? counting.left : (v?.win ?? 0);
  const balance = counting ? counting.balance : (v?.balance ?? null);
  const hint =
    message ?? (phase === "hold" ? t.poker.holdHint : t.poker.startHint);

  return (
    <section class="poker">
      <div class="pk-head">
        <h2>🃏 {t.poker.title}</h2>
        <a class="btn btn-small" href="#/tables">
          {t.poker.back}
        </a>
      </div>
      <div class="pk-machine">
        <table class="pk-paytable">
          <tbody>
            {POKER_HANDS.map((h) => (
              <tr
                class={
                  v?.made === h && phase !== "idle"
                    ? phase === "hold"
                      ? "pk-made"
                      : "pk-won"
                    : ""
                }
                key={h}
              >
                <th>{t.poker.hands[h]}</th>
                <td>{PAYTABLE[h] * shownBet}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div class="pk-leds">
          <div>
            <span>{t.poker.bet}</span>
            <strong>{shownBet}</strong>
          </div>
          <div class="pk-led-win">
            <span>{t.poker.win}</span>
            <strong>{win}</strong>
          </div>
          <div>
            <span>{t.poker.balance}</span>
            <strong>{balance ?? "–"}</strong>
          </div>
        </div>

        {/* The doubling card takes the place of the hand, as on the real machine (and on a phone the buttons stay in view). */}
        {doubling || (v?.double && phase !== "hold") ? (
          <div class="pk-double">
            <div class="pk-double-card">
              {suspense || !v?.double || (doubling && busy) ? (
                <span
                  class={`pcard back ${suspense ? "pk-shake" : ""}`}
                  aria-hidden="true"
                />
              ) : (
                <span
                  class="pk-in"
                  key={`d${dealNo}-${v.double.card}-${v.win}`}
                >
                  <CardFace card={v.double.card} />
                </span>
              )}
            </div>
            <div class="pk-double-info">
              {t.poker.double} · max {(TOP_MULTIPLIER * shownBet) / 2}
            </div>
          </div>
        ) : (
          <div class="pk-cards">
            {[0, 1, 2, 3, 4].map((i) => {
              const c = v?.hand?.[i];
              return (
                <button
                  type="button"
                  class={`pk-slot ${held.has(i) ? "pk-held" : ""}`}
                  onClick={() => toggle(i)}
                  disabled={phase !== "hold"}
                  aria-pressed={held.has(i)}
                  key={i}
                >
                  {c === undefined ? (
                    <span class="pcard back" aria-hidden="true" />
                  ) : (
                    <span
                      class={fresh.has(i) ? "pk-in" : ""}
                      style={{
                        animationDelay: `${[...fresh].indexOf(i) * CARD_STEP_MS}ms`,
                      }}
                      key={`${dealNo}-${i}`}
                    >
                      <CardFace card={c} />
                    </span>
                  )}
                  <span class="pk-held-label">
                    {held.has(i) ? t.poker.held : " "}
                  </span>
                </button>
              );
            })}
          </div>
        )}

        <p class="pk-hint" role="status">
          {hint}
        </p>
        {error && <p class="error">{error}</p>}

        <div class="pk-buttons">
          {doubling ? (
            <>
              <button
                type="button"
                class="pk-btn pk-small"
                onClick={() => void guess("small")}
                disabled={busy}
              >
                {t.poker.small}
              </button>
              <button
                type="button"
                class="pk-btn pk-big"
                onClick={() => void guess("big")}
                disabled={busy}
              >
                {t.poker.big}
              </button>
              <button
                type="button"
                class="pk-btn pk-collect"
                onClick={() => void collect()}
                disabled={busy}
              >
                {t.poker.collect}
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                class="pk-btn"
                onClick={nextBet}
                disabled={phase === "hold" || busy}
              >
                {t.poker.bet} {bet}
              </button>
              <button
                type="button"
                class="pk-btn pk-double-btn"
                onClick={startDouble}
                disabled={!v?.canDouble || busy}
              >
                {t.poker.double}
              </button>
              <button
                type="button"
                class="pk-btn pk-collect"
                onClick={() => void collect()}
                disabled={phase !== "win" || busy}
              >
                {t.poker.collect}
              </button>
              <button
                type="button"
                class="pk-btn pk-deal"
                onClick={() => void (phase === "hold" ? draw() : deal())}
                disabled={busy || Boolean(counting)}
              >
                {phase === "hold" ? t.poker.draw : t.poker.deal}
              </button>
            </>
          )}
        </div>
      </div>
      <p class="pk-keys muted">{t.poker.keys}</p>
      <details class="pk-rules card">
        <summary>📖 {t.rules.buttonShort}</summary>
        <ul>
          {t.poker.rules.map((r) => (
            <li key={r}>{r}</li>
          ))}
        </ul>
      </details>
    </section>
  );
}
